

class NSSTabsGroup extends HTMLElement {

  constructor() {
    super();

    this.debug = false;

    this.els = {}

    this.enableAutoswitch = "false";
    this.autoswitchDelay = 3000;
    this.autoswitchPause = false;

    // "switch" (default) = clicking a control swaps the visible tab group.
    // "scroll" = all groups render stacked; clicking a control scrolls to it
    // and the active control tracks scroll position (scrollspy).
    this.behavior = "switch";
  }

  connectedCallback() {
    if (this.debug) console.log(`[NSS][Tabs Group] Connected.`);

    this.behavior = this.getAttribute(`nss-controls-behavior`) || `switch`;

    this.init();

    // Scroll mode runs a scrollspy (set up in init) instead of autoplay; the
    // active control tracks which section is under the pinned controls bar.
    if (this.behavior !== `scroll`) {
      this.initAutoplay();
    }

    this.addEventListener(`click`, this.handleClick.bind(this));
    this.addEventListener(`mouseenter`, this.handleMouseEnter.bind(this));
    this.addEventListener(`mouseleave`, this.handleMouseLeave.bind(this));

    const editorEvents = [
      `shopify:inspector:activate`,
      `shopify:inspector:deactivate`,
      `shopify:section:load`,
      `shopify:section:unload`,
      `shopify:section:select`,
      `shopify:section:deselect`,
      `shopify:section:reorder`,
      `shopify:block:select`,
      `shopify:block:deselect`,
    ];
    editorEvents.forEach((event) => {
      this.addEventListener(event, this.handleEditorEvent.bind(this));
    });
  }

  init() {
    this.els.controlScroll = this.querySelector(`.nss-tabs-controls__scroll-wrapper`);
    this.els.controls = this.querySelectorAll(`nss-tab-control`);
    this.els.groups = this.querySelectorAll(`nss-tab-group`);

    // Wrap all tab controls in a single container div (mega nav only)
    if (this.classList.contains(`nss-mega-nav-tabs`) && !this.querySelector(`.nss-tab-controls-wrapper`) && this.els.controls.length) {
      const wrapper = document.createElement(`div`);
      wrapper.className = `nss-tab-controls-wrapper`;
      this.prepend(wrapper);
      this.els.controls.forEach(ctrl => wrapper.appendChild(ctrl));
    }
    this.els.controlsWrapper = this.querySelector(`.nss-tab-controls-wrapper`);
    this.els.controlsBar = this.querySelector(`nss-tabs-controls`);

    this.enableAutoswitch = this.getAttribute(`nss-enable-autoswitch`);
    this.autoswitchDelay = this.getAttribute(`nss-autoswitch-delay`);

    // Scroll mode: keep every group visible (CSS handles layout); mark the
    // first control active as a baseline, then let the scrollspy take over.
    if (this.behavior === `scroll`) {
      const firstId = this.els.controls[0] && this.els.controls[0].getAttribute(`nss-tab-control-id`);
      if (firstId != null) this.setActiveControl(firstId);
      this.initScrollSpy();
      return;
    }

    // Activate first tab if none is already active
    const activeControl = [...this.els.controls].find(c => c.classList.contains(`nss-active`));
    if (!activeControl && this.els.controls[0]) {
      const id = this.els.controls[0].getAttribute(`nss-tab-control-id`);
      this.selectTabByControlId(id, { scrollControl: false });
    } else if (activeControl) {
      const wrapperEl = activeControl.querySelector(`.nss-tab-control__wrapper`);
      this.applyExternalContent(wrapperEl);
    }
  }

  initAutoplay() {
    if (this.enableAutoswitch === "false") return;

    if (this.debug) console.log(`[NSS][Tabs Group] this.enableAutoswitch:`, this.enableAutoswitch);
    if (this.debug) console.log(`[NSS][Tabs Group] this.autoswitchDelay:`, this.autoswitchDelay);

    let current = 0;
    let max = this.els.controls.length;
    // let scrollOptions = {
    //   behavior: "smooth",
    //   block: "center",
    //   inline: "center",
    //   container: "nearest",
    // };

    setInterval(() => {
      if (this.autoswitchPause) return;

      current += 1;
      if (current == max) current = 0;

      /**
       * Safari doesn't support 'container' in scrollIntoView's options. This
       * means it effects every scroll container, causing the entire page to
       * scroll to this element at every call.
       */
      // this.els.controls[current].scrollIntoView(scrollOptions);

      this.scrollActiveControlIntoView(this.els.controls[current]);
      const id = this.els.controls[current].getAttribute(`nss-tab-control-id`);
      this.selectTabByControlId(id, { scrollControl: false });
    }, this.autoswitchDelay);
  }

  handleEditorEvent(e) {
    if (this.debug) console.log(`[NSS][Tabs Group] Handle editor event, e:`, e);

    switch (e.type) {
      case `shopify:block:select`:
        if (e.detail.blockId === this.getAttribute(`data-block-id`)) return;

        const markedEl = this.querySelector(`[data-block-id="${e.detail.blockId}"]`);
        if (!markedEl) return;

        const controlEl = markedEl.closest(`nss-tab-control`);
        if (controlEl && this.contains(controlEl)) {
          const id = controlEl.getAttribute(`nss-tab-control-id`);
          if (id != null) {
            this.selectTabByControlId(id, { scrollControl: true });
          }
          return;
        }

        const groupEl = markedEl.closest(`nss-tab-group`);
        if (groupEl && this.contains(groupEl)) {
          const id = groupEl.getAttribute(`nss-tab-group-id`);
          if (id != null) {
            this.selectTabByControlId(id, { scrollControl: true });
          }
        }
        break;
    }
  }

  selectTabByControlId(id, options = {}) {
    const controlEl = this.querySelector(`nss-tab-control[nss-tab-control-id="${id}"]`);
    const groupEl = this.querySelector(`nss-tab-group[nss-tab-group-id="${id}"]`);
    if (!controlEl || !groupEl) return;

    this.els.controls.forEach((c) => c.classList.remove(`nss-active`));
    this.els.groups.forEach((g) => g.classList.remove(`nss-active`));

    const cardEls = groupEl.querySelectorAll(`.nss-card-product__wrapper`);
    cardEls.forEach((cardEl) => cardEl.classList.remove(`nss-has-hovered`));

    controlEl.classList.add(`nss-active`);
    groupEl.classList.add(`nss-active`);

    const wrapperEl = controlEl.querySelector(`.nss-tab-control__wrapper`);
    if (wrapperEl) {
      this.applyExternalContent(wrapperEl);
    }

    this.updateAllCarousels();
    this.stopAllVideos();

    if (options.scrollControl) {
      this.scrollActiveControlIntoView(controlEl);
    }
  }

  scrollActiveControlIntoView(controlEl) {
    const scrollEl = this.els.controlScroll;
    if (!scrollEl || !controlEl) return;

    const scrollRect = scrollEl.getBoundingClientRect();
    const rect = controlEl.getBoundingClientRect();
    const x = controlEl.offsetLeft;
    const newLeft = x - (scrollRect.width / 2) + (rect.width / 2);

    scrollEl.scrollTo({
      behavior: `smooth`,
      top: 0,
      left: newLeft,
    });
  }

  handleClick(e) {
    const controlEl = e.target.closest(`nss-tab-control`);
    if (!controlEl || !this.contains(controlEl)) return;

    const id = controlEl.getAttribute(`nss-tab-control-id`);
    if (id == null) return;

    if (this.behavior === `scroll`) {
      this.scrollToGroup(id);
      return;
    }

    this.selectTabByControlId(id, { scrollControl: false });
  }

  // --- Scroll mode --------------------------------------------------------

  setActiveControl(id) {
    this.els.controls.forEach((c) =>
      c.classList.toggle(`nss-active`, c.getAttribute(`nss-tab-control-id`) === String(id))
    );
  }

  // Mark the control whose section is in view active as the page scrolls.
  initScrollSpy() {
    this.scrollSpyTicking = false;
    this.onScrollSpy = () => {
      if (this.scrollSpyTicking) return;
      this.scrollSpyTicking = true;
      window.requestAnimationFrame(() => {
        this.updateScrollSpy();
        this.scrollSpyTicking = false;
      });
    };

    window.addEventListener(`scroll`, this.onScrollSpy, { passive: true });
    window.addEventListener(`resize`, this.onScrollSpy, { passive: true });
    this.updateScrollSpy();
  }

  updateScrollSpy() {
    if (!this.els.groups || !this.els.groups.length) return;

    // Activation line below the controls bar — larger SENSITIVITY switches sooner.
    const SENSITIVITY = 0.4;
    const barBottom = this.getScrollAnchorOffset();
    const anchor = barBottom + Math.min(window.innerHeight * SENSITIVITY, window.innerHeight * 0.5 - barBottom);

    let activeId = this.els.groups[0].getAttribute(`nss-tab-group-id`);
    this.els.groups.forEach((groupEl) => {
      if (groupEl.getBoundingClientRect().top - anchor <= 1) {
        activeId = groupEl.getAttribute(`nss-tab-group-id`);
      }
    });

    // At the bottom of the page force the last section active.
    const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
    if (atBottom) {
      activeId = this.els.groups[this.els.groups.length - 1].getAttribute(`nss-tab-group-id`);
    }

    this.setActiveControl(activeId);
  }

  disconnectedCallback() {
    if (this.onScrollSpy) {
      window.removeEventListener(`scroll`, this.onScrollSpy);
      window.removeEventListener(`resize`, this.onScrollSpy);
    }
  }

  getStickyOffset() {
    // Prefer the controls bar's resolved sticky `top` so the offset stays
    // correct across breakpoints; fall back to the liquid-set attribute.
    if (this.els.controlsBar) {
      const top = parseFloat(getComputedStyle(this.els.controlsBar).top);
      if (!isNaN(top)) return top;
    }
    const attr = parseInt(this.getAttribute(`nss-sticky-offset`), 10);
    return isNaN(attr) ? 0 : attr;
  }

  // Distance from the top of the viewport to the bottom of the (sticky)
  // controls bar — used so a scrolled-to section heading lands just below it.
  getScrollAnchorOffset(extra = 16) {
    const controlsH = this.els.controlsBar ? this.els.controlsBar.getBoundingClientRect().height : 0;
    return this.getStickyOffset() + controlsH + extra;
  }

  scrollToGroup(id) {
    const groupEl = this.querySelector(`nss-tab-group[nss-tab-group-id="${id}"]`);
    if (!groupEl) return;

    // Don't force the control active here — the scrollspy sets the active
    // control based on the section actually in view as the page scrolls.
    const y = groupEl.getBoundingClientRect().top + window.scrollY - this.getScrollAnchorOffset();
    window.scrollTo({ top: Math.max(0, y), behavior: `smooth` });
  }

  handleMouseEnter(e) {
    this.autoswitchPause = true;
  }

  handleMouseLeave(e) {
    this.autoswitchPause = false;
  }

  applyExternalContent(wrapperEl) {
    const externalSettings = wrapperEl.getAttribute(`nss-external-settings`);
    if (externalSettings === "true") {
      const extId = wrapperEl.getAttribute(`nss-external-target-id`);
      const extColorBg = wrapperEl.getAttribute(`nss-external-color-bg`);
      const extColorText = wrapperEl.getAttribute(`nss-external-color-text`);
      const extImageBg = wrapperEl.getAttribute(`nss-external-image-bg`);

      const extEl = document.querySelector(`#${extId}`);
      if (extEl) {
        extEl.style.backgroundColor = extColorBg;
        extEl.style.color = extColorText;

        if (extImageBg) {
          extEl.style.backgroundImage = `url('${extImageBg}')`;
        }
      }
    }
  }

  updateAllCarousels() {
    const carouselEls = this.querySelectorAll(`nss-carousel`);

    carouselEls.forEach((carouselEl) => {
      carouselEl.swiper.update();
    });
  }

  stopAllVideos() {
    const videoEls = this.querySelectorAll(`video`);

    videoEls.forEach((videoEl) => {
      if (videoEl.getAttribute(`no-stop`) === "true") return;
      videoEl.pause();
      videoEl.muted = true;
      videoEl.parentElement.setAttribute(`nss-video-playing`, false);
    });
  }
}

let elementName = `nss-tabs-group`;
if (typeof customElements.get(elementName) === `undefined`) {
  customElements.define(elementName, NSSTabsGroup);
} else {
  console.warn(`[NSS] Element '${elementName}' already defined.`)
}